import { useEffect, useRef } from 'react';
import { FileUploadOutlined, Person } from '@mui/icons-material';
import { FormCard } from '@practium/ui';
import type { UserPageMode } from '../../types';
import styles from './AvatarCard.module.css';

interface AvatarCardProps {
	mode: UserPageMode;
	file: File | null;
	onFileChange: (file: File | null) => void;
	personName?: string;
	avatarUrl?: string;
	onEditRequest?: () => void;
}

export const AvatarCard = ({
	mode,
	file,
	onFileChange,
	personName,
	avatarUrl,
	onEditRequest,
}: AvatarCardProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const shouldAutoOpenRef = useRef(false);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selected = e.target.files?.[0] ?? null;
		onFileChange(selected);
	};

	useEffect(() => {
		if (mode !== 'view' && shouldAutoOpenRef.current) {
			shouldAutoOpenRef.current = false;
			inputRef.current?.click();
		}
	}, [mode]);

	if (mode === 'view') {
		const canEditPhoto = !!onEditRequest;
		const handleViewClick = () => {
			if (!canEditPhoto) return;
			shouldAutoOpenRef.current = true;
			onEditRequest?.();
		};

		return (
			<div className={styles.sideCard}>
				<FormCard icon={<Person />} title="Foto" className={styles.card}>
					<div
						className={`${styles.viewArea} ${avatarUrl ? styles.hasImage : ''} ${canEditPhoto ? styles.clickable : ''}`}
						onClick={canEditPhoto ? handleViewClick : undefined}
						role={canEditPhoto ? 'button' : undefined}
						tabIndex={canEditPhoto ? 0 : undefined}
						onKeyDown={canEditPhoto ? (e) => e.key === 'Enter' && handleViewClick() : undefined}
					>
						{avatarUrl ? (
							<div className={styles.previewWrapper}>
								<img src={avatarUrl} alt={personName ?? 'Avatar'} className={styles.previewImage} />
								{personName && (
									<span className={styles.badge}>
										<span className={styles.badgeDot}>&#9679;</span>
										{personName}
									</span>
								)}
							</div>
						) : (
							<>
								<svg viewBox="0 0 80 80" className={styles.silhouette} aria-hidden="true">
									<circle cx="40" cy="28" r="18" />
									<ellipse cx="40" cy="72" rx="28" ry="18" />
								</svg>
								{personName && (
									<span className={styles.badge}>
										<span className={styles.badgeDot}>&#9679;</span>
										{personName}
									</span>
								)}
							</>
						)}
					</div>
				</FormCard>
			</div>
		);
	}

	const previewSrc = file ? URL.createObjectURL(file) : (avatarUrl ?? null);
	const hasPreview = previewSrc !== null;

	return (
		<div className={styles.sideCard}>
			<FormCard icon={<Person />} title="Foto" className={styles.card}>
				<div
					className={`${styles.uploadArea} ${hasPreview ? styles.hasImage : ''}`}
					onClick={() => inputRef.current?.click()}
					role="button"
					tabIndex={0}
					onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
				>
					{hasPreview ? (
						<div className={styles.previewWrapper}>
							<img src={previewSrc} alt="Preview" className={styles.previewImage} />
							<FileUploadOutlined className={styles.uploadIconOverlay} />
							<span className={styles.badge}>
								<span className={styles.badgeDot}>&#9679;</span>
								{file ? file.name : (personName ?? '')}
							</span>
						</div>
					) : (
						<div className={styles.uploadContent}>
							<FileUploadOutlined className={styles.uploadIcon} />
						</div>
					)}
					<input
						ref={inputRef}
						type="file"
						accept="image/*"
						className={styles.hiddenInput}
						onChange={handleFileChange}
					/>
				</div>
			</FormCard>
		</div>
	);
};
